import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, CircularProgress, Popover, Stack, TextField, Typography } from '@mui/material';

type AddCommentPopoverProps<RowType extends object = object> = {
  open: boolean;
  anchorEl: HTMLElement | null;
  row: RowType | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (commentText: string, row: RowType) => Promise<void> | void;
};

export const AddCommentPopover = <RowType extends object = object>({
  open,
  anchorEl,
  row,
  loading = false,
  onClose,
  onSubmit,
}: AddCommentPopoverProps<RowType>) => {
  const intl = useIntl();
  const [commentText, setCommentText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async () => {
    const trimmed = commentText.trim();
    if (!trimmed || !row || errorMessage) return;

    await onSubmit(trimmed, row);
  };

  useEffect(() => {
    if (!open) {
      setCommentText('');
      setErrorMessage('');
    }
  }, [open]);

  useEffect(() => {
    commentText.trim()?.length > 250
      ? setErrorMessage(
          intl.formatMessage(
            { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
            { max: 250 }
          )
        )
      : setErrorMessage('');
  }, [commentText, intl]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            p: 2,
          },
        },
      }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">
          <FormattedMessage id="projectStepManaging.addComment" defaultMessage="Yorum Ekle" />
        </Typography>

        <TextField
          autoFocus
          multiline
          minRows={4}
          maxRows={8}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'projectStepManaging.writeComment',
            defaultMessage: 'Yorumunuzu yazın',
          })}
          error={!!errorMessage}
          helperText={errorMessage}
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              height: 'auto',
            },
          }}
        />

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button onClick={onClose} disabled={loading}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}>
            <FormattedMessage id="dashboard.save" defaultMessage="Kaydet" />
          </Button>
        </Box>
      </Stack>
    </Popover>
  );
};

export default AddCommentPopover;
